import { computed, inject } from '@angular/core';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { combineLatest, pipe, switchMap, tap } from 'rxjs';

import { KpiMaster, KpisMastersInput } from '@/core/models/kpi.interface';
import { KPIService } from '@/core/services/kpi.service';
import { EventMasterName } from '@/shared/enums/event-masters.enum';
import { KpiMasterName } from '@/shared/enums/kpi-master.enum';
import { EventMaster, EventsMastersInput } from '@/shared/models/event-master.model';
import { LoadingState } from '@/shared/models/loading-state.model';
import { Season } from '@/shared/models/season.model';
import { CompetitionsService } from '@/shared/services/competitions.service';
import { EventMastersService } from '@/shared/services/event-masters.service';
import { PhasesMastersService } from '@/shared/services/phasesMasters.service';
import { SeasonsService } from '@/shared/services/seasons.service';

import { ThemeService } from '../theme';

export interface MainState {
  currentSeason: Season | null;
  defaultCompetition: number | null;
  seasons: Season[];
  kpis: {
    kpiMasterKeys: KpiMaster[];
  };
  events: {
    masters: EventMaster[];
  };
  loadingState: LoadingState;
}

const initialState: MainState = {
  currentSeason: null,
  defaultCompetition: null,
  seasons: [],
  kpis: {
    kpiMasterKeys: [],
  },
  events: {
    masters: [],
  },
  loadingState: {
    isLoading: true,
    error: null,
  },
};

export const MainStore = signalStore(
  { providedIn: 'root' },
  withDevtools('main'),
  withState(initialState),

  withProps(() => ({
    kpiService: inject(KPIService),
    eventMasterService: inject(EventMastersService),
    phasesMastersService: inject(PhasesMastersService),
    seasonService: inject(SeasonsService),
    competitionsService: inject(CompetitionsService),
    domainConfig: inject(ThemeService),
  })),

  withComputed((store) => ({
    teamAnalysisKpiMasterIds: computed(() => {
      return store
        .kpis()
        .kpiMasterKeys.filter((kpi) => {
          return (
            // General
            kpi.name.toLowerCase() === KpiMasterName.GENERAL_BALL_IN_PLAY_TIME ||
            // Attack
            kpi.name.toLowerCase() === KpiMasterName.ATTACK_SHOTS ||
            kpi.name.toLowerCase() === KpiMasterName.ATTACK_XG ||
            kpi.name.toLowerCase() === KpiMasterName.ATTACK_DEEP_PROGRESSIONS ||
            kpi.name.toLowerCase() === KpiMasterName.ATTACK_PASSES_INTO_THE_BOX ||
            kpi.name.toLowerCase() === KpiMasterName.ATTACK_VAAP ||
            kpi.name.toLowerCase() === KpiMasterName.ATTACK_EPV ||
            // Defense

            kpi.name.toLowerCase() === KpiMasterName.DEFENSE_PERCENT_DEFENSIVE_TIME ||
            // ABP
            kpi.name.toLowerCase() === KpiMasterName.ABP_CORNERS ||
            kpi.name.toLowerCase() === KpiMasterName.ABP_SHOTS_GOALS_FROM_CORNERS ||
            kpi.name.toLowerCase() === KpiMasterName.ABP_FREE_KICKS ||
            kpi.name.toLowerCase() === KpiMasterName.ABP_SHOTS_GOALS_FROM_FREE_KICKS ||
            kpi.name.toLowerCase() === KpiMasterName.ABP_THROW_INS_XG ||
            kpi.name.toLowerCase() === KpiMasterName.ABP_SHOTS_GOALS_FROM_THROW_INS ||
            kpi.name.toLowerCase() === KpiMasterName.ABP_PENALTIES
          );
        })
        .map((kpi) => kpi.id);
    }),
    teamGeneralReviewKpiMasterIds: computed(() => {
      return store
        .kpis()
        .kpiMasterKeys.filter((kpi) => {
          return (
            kpi.name.toLowerCase() === KpiMasterName.YELLOW_CARDS ||
            kpi.name.toLowerCase() === KpiMasterName.RED_CARDS ||
            kpi.name.toLowerCase() === KpiMasterName.X_WIN ||
            kpi.name.toLowerCase() === KpiMasterName.XG
          );
        })
        .map((kpi) => kpi.id);
    }),
    teamGeneralReviewPerformanceKpiMasterIds: computed(() => {
      return store
        .kpis()
        .kpiMasterKeys.filter((kpi) => {
          return (
            // Attack
            kpi.name.toLowerCase() === KpiMasterName.RHYTHM ||
            kpi.name.toLowerCase() === KpiMasterName.PRODUCTION ||
            kpi.name.toLowerCase() === KpiMasterName.COMPLETION_EFFE ||
            kpi.name.toLowerCase() === KpiMasterName.ATTACK_SET_PIECES ||
            // Defense
            kpi.name.toLowerCase() === KpiMasterName.INTERRUPTION_TIME ||
            kpi.name.toLowerCase() === KpiMasterName.AVOIDANCE ||
            kpi.name.toLowerCase() === KpiMasterName.AVOIDANCE_EFFE ||
            kpi.name.toLowerCase() === KpiMasterName.DEFENSE_SET_PIECES
          );
        })
        .map((kpi) => kpi.id);
    }),
    playersKpiMasterIdsGeneral: computed(() => {
      return store
        .kpis()
        .kpiMasterKeys.filter((kpi) => {
          return (
            // General
            kpi.name.toLowerCase() === KpiMasterName.MINUTES_PLAYED ||
            kpi.name.toLowerCase() === KpiMasterName.RED_CARDS ||
            kpi.name.toLowerCase() === KpiMasterName.YELLOW_CARDS ||
            // Defense
            kpi.name.toLowerCase() === KpiMasterName.GOALS ||
            kpi.name.toLowerCase() === KpiMasterName.DEFENSE_PERCENT_DEFENSIVE_TIME ||
            kpi.name.toLowerCase() === KpiMasterName.DEFENSE_PRESSURES ||
            kpi.name.toLowerCase() === KpiMasterName.DEFENSE_PRESSURES_REGAINS ||
            kpi.name.toLowerCase() === KpiMasterName.DEFENSE_DISTANCE ||
            kpi.name.toLowerCase() === KpiMasterName.DEFENSE_AGGRESSIVE_ACTIONS ||
            kpi.name.toLowerCase() === KpiMasterName.DEFENSE_VAAP ||
            kpi.name.toLowerCase() === KpiMasterName.DEFENSE_EPV ||
            // Attack

            kpi.name.toLowerCase() === KpiMasterName.ATTACKING_TIME ||
            kpi.name.toLowerCase() === KpiMasterName.ATTACK_SHOTS ||
            kpi.name.toLowerCase() === KpiMasterName.ATTACK_XG ||
            kpi.name.toLowerCase() === KpiMasterName.ATTACK_DEEP_PROGRESSIONS ||
            kpi.name.toLowerCase() === KpiMasterName.ATTACK_PASSES_INTO_THE_BOX ||
            kpi.name.toLowerCase() === KpiMasterName.ATTACK_VAAP ||
            kpi.name.toLowerCase() === KpiMasterName.ATTACK_EPV ||
            // ABP
            kpi.name.toLowerCase() === KpiMasterName.ABP_CORNERS ||
            kpi.name.toLowerCase() === KpiMasterName.ABP_SHOTS_GOALS_FROM_CORNERS ||
            kpi.name.toLowerCase() === KpiMasterName.ABP_FREE_KICKS ||
            kpi.name.toLowerCase() === KpiMasterName.ABP_SHOTS_GOALS_FROM_FREE_KICKS ||
            kpi.name.toLowerCase() === KpiMasterName.ABP_THROW_INS_XG ||
            kpi.name.toLowerCase() === KpiMasterName.ABP_SHOTS_GOALS_FROM_THROW_INS ||
            kpi.name.toLowerCase() === KpiMasterName.ABP_PENALTIES
          );
        })
        .map((kpi) => kpi.id);
    }),

    // Events
    eventsMastersIds: computed(() => {
      return store
        .events()
        .masters.filter((event) => {
          return (
            event.name.toLowerCase() === EventMasterName.GOAL ||
            event.name.toLowerCase() === EventMasterName.SUBSTITUTION
          );
        })
        .map((event) => event.id);
    }),
    eventsMastersIdsPlayers: computed(() => {
      return store
        .events()
        .masters.filter((event) => {
          return (
            event.name.toLowerCase() === EventMasterName.SUBSTITUTION ||
            event.name.toLowerCase() === EventMasterName.INJURY_STOPPAGE ||
            event.name.toLowerCase() === EventMasterName.STARTING_XI
          );
        })
        .map((event) => event.id);
    }),
  })),

  withMethods((store) => ({
    loadDefaultCompetition: rxMethod<{ season: Season; team: number }>(
      pipe(
        switchMap(({ season, team }) => store.competitionsService.getCompetitionInscriptions(null, season.id, team)),
        tap((inscriptions) => {
          const defaultCompetition = inscriptions.filter((i) => i.isDefault).map((i) => i.competitionId);
          patchState(store, {
            defaultCompetition: defaultCompetition.length ? defaultCompetition[0] : null,
          });
        })
      )
    ),
    updateCurrentSeason(season: Season): void {
      patchState(store, { currentSeason: season });
    },
    loadMultipleKpis: rxMethod<{
      kpisMastersFilter?: KpisMastersInput;
      eventsMastersFilter?: EventsMastersInput;
    } | void>(
      pipe(
        tap(() => {
          patchState(store, (state) => ({
            ...state,
            loadingState: {
              isLoading: true,
              error: null,
            },
          }));
        }),
        switchMap((filters) => {
          return combineLatest({
            kpisMasters: store.kpiService.getKpisMasters(
              filters && filters.kpisMastersFilter ? filters.kpisMastersFilter : {}
            ),
            eventsMasters: store.eventMasterService.getEventMasters(
              filters && filters.eventsMastersFilter ? filters.eventsMastersFilter : {}
            ),
            seasons: store.seasonService.getSeasons(),
          });
        }),
        tap((results: { kpisMasters: KpiMaster[]; eventsMasters: EventMaster[]; seasons: Season[] }) => {
          patchState(store, (state) => ({
            ...state,
            kpis: {
              ...state.kpis,
              kpiMasterKeys: results.kpisMasters,
            },
            events: {
              ...state.events,
              masters: results.eventsMasters,
            },
            seasons: results.seasons,
            loadingState: {
              isLoading: false,
              error: null,
            },
          }));
        })
      )
    ),
  }))
);
